<template>
  <div class="px-2">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#9E6D2B"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />
    <v-snackbar v-model="showsnackbar" color="#9E6D2B" right :timeout="timeout">
      <span style="color: #FFFFFF; font-weight: 600;">{{ msg }}</span>
      <template v-slot:action="{ attrs }">
        <v-btn text color="#FFFFFF" v-bind="attrs" @click="showsnackbar = false">
          <v-icon style="color: #FFFFFF">mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <!-- TOP HEADER & CONTROLS -->
    <v-row align="center" justify="space-between" class="mb-3">
      <v-col cols="12" sm="4" md="4" class="py-1">
        <span class="mainHeader">Customer Advances</span>
      </v-col>
      <v-col cols="12" sm="8" md="8" class="d-flex align-center justify-end py-1" style="gap: 12px; flex-wrap: wrap;">
        <!-- From Date -->
        <div style="width: 150px;">
          <v-menu
            ref="fromMenu"
            v-model="fromMenu"
            :close-on-content-click="false"
            :return-value.sync="from"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="from"
                label="From"
                outlined
                dense
                readonly
                hide-details
                background-color="#FFFFFF"
                v-bind="attrs"
                v-on="on"
                prepend-inner-icon="mdi-calendar"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="from"
              no-title
              scrollable
              color="#9E6D2B"
              @change="$refs.fromMenu.save(from)"
            >
              <v-spacer></v-spacer>
            </v-date-picker>
          </v-menu>
        </div>

        <!-- To Date -->
        <div style="width: 150px;">
          <v-menu
            ref="toMenu"
            v-model="toMenu"
            :close-on-content-click="false"
            :return-value.sync="to"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="to"
                label="To"
                outlined
                dense
                readonly
                hide-details
                background-color="#FFFFFF"
                v-bind="attrs"
                v-on="on"
                prepend-inner-icon="mdi-calendar"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="to"
              no-title
              scrollable
              color="#9E6D2B"
              @change="$refs.toMenu.save(to)"
            >
              <v-spacer></v-spacer>
            </v-date-picker>
          </v-menu>
        </div>

        <!-- Today Quick Button -->
        <button class="btn-today" @click="setToday" title="Set to Today">
          Today
        </button>

        <!-- Download PDF Button -->
        <v-btn
          color="#9E6D2B"
          dark
          height="40"
          elevation="1"
          class="text-none font-weight-bold px-4"
          style="border-radius: 8px;"
          @click="download"
          :disabled="appLoading"
        >
          <v-icon left small>mdi-file-pdf-box</v-icon>
          Download PDF
        </v-btn>
      </v-col>
    </v-row>

    <!-- PRO KPI METRIC CARDS -->
    <v-row class="mb-4">
      <v-col cols="12" sm="6" md="4" class="py-1">
        <v-card outlined class="pa-3 rounded-lg flex-column justify-center" color="#FFFFFF">
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="kpi-title">TOTAL ADVANCES COUNT</span>
            <v-icon small color="#9E6D2B">mdi-hand-coin-outline</v-icon>
          </div>
          <div class="kpi-val">{{ filteredAdvances.length }} <small class="text-caption">Bills</small></div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="4" class="py-1">
        <v-card outlined class="pa-3 rounded-lg flex-column justify-center" color="#FFFFFF">
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="kpi-title">TOTAL ADVANCE AMOUNT</span>
            <v-icon small color="#047857">mdi-cash-multiple</v-icon>
          </div>
          <div class="kpi-val text-green">₹{{ formatCurrency(computedTotalAmount) }}</div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="4" class="py-1">
        <v-card outlined class="pa-3 rounded-lg flex-column justify-center" color="#FFFFFF">
          <div class="d-flex align-center justify-space-between mb-1">
            <span class="kpi-title">CASH ADVANCES</span>
            <v-icon small color="#2563EB">mdi-cash-fast</v-icon>
          </div>
          <div class="kpi-val text-blue">₹{{ formatCurrency(computedCashAmount) }}</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- FILTER & SEARCH BAR -->
    <div class="filter-search-wrap mb-4">
      <div class="type-filter-group">
        <button
          class="type-pill"
          :class="{ active: selectedMode === 'ALL' }"
          @click="selectedMode = 'ALL'"
        >
          All Modes
        </button>
        <button
          class="type-pill"
          :class="{ active: selectedMode === 'Cash' }"
          @click="selectedMode = 'Cash'"
        >
          Cash
        </button>
        <button
          class="type-pill"
          :class="{ active: selectedMode === 'Bank' }"
          @click="selectedMode = 'Bank'"
        >
          Bank / Digital
        </button>
      </div>

      <div class="search-input-wrap">
        <v-text-field
          v-model="searchQuery"
          prepend-inner-icon="mdi-magnify"
          placeholder="Search bill no or mode..."
          outlined
          dense
          hide-details
          clearable
          class="search-field"
        ></v-text-field>
      </div>
    </div>

    <!-- NO DATA STATE -->
    <div v-if="!appLoading && filteredAdvances.length === 0" class="no-data-box text-center py-8 my-4">
      <v-icon color="#9E6D2B" class="mb-2">mdi-information-outline</v-icon>
      <div class="no-data-text">No customer advances found for the selected date range.</div>
    </div>

    <!-- MAIN ADVANCES REPORT TABLE CARD -->
    <v-card v-if="filteredAdvances.length > 0" outlined class="rounded-lg overflow-hidden elevation-1 mb-5" color="#FFFFFF">
      <div class="table-container">
        <table class="pro-advances-table">
          <thead>
            <tr>
              <th class="text-left sticky-col">BILL NO</th>
              <th class="text-center">DATE</th>
              <th class="text-center">PAYMENT MODE</th>
              <th class="text-right">ADVANCE AMOUNT (₹)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, rIdx) in filteredAdvances" :key="rIdx" class="table-row">
              <td class="text-left sticky-col font-weight-bold">
                <span class="bill-code-pill">{{ row.billNo || '-' }}</span>
              </td>
              <td class="text-center text-muted font-weight-medium">
                {{ row.date || '-' }}
              </td>
              <td class="text-center">
                <span class="mode-badge" :class="getModeBadgeClass(row.mode)">
                  {{ row.mode || 'Cash' }}
                </span>
              </td>
              <td class="text-right font-weight-bold gold-amount-text">
                ₹{{ formatCurrency(row.amount) }}
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="totals-row">
              <td class="sticky-col total-label-cell">TOTAL ADVANCES</td>
              <td class="text-center"><strong>{{ filteredAdvances.length }} Bills</strong></td>
              <td></td>
              <td class="text-right total-amount-cell">
                <strong>₹{{ formatCurrency(computedTotalAmount) }}</strong>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </v-card>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "AdvancesReportPage",
  data() {
    var today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .substr(0, 10);
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 3000,
      msg: null,
      fromMenu: false,
      toMenu: false,
      from: today,
      to: today,
      advanceRows: [],
      totals: { amount: 0 },
      selectedMode: "ALL",
      searchQuery: "",
    };
  },
  computed: {
    filteredAdvances() {
      let result = this.advanceRows;
      if (this.selectedMode !== "ALL") {
        if (this.selectedMode === "Bank") {
          result = result.filter(r => (r.mode || "").toLowerCase() !== "cash");
        } else {
          result = result.filter(r => (r.mode || "").toLowerCase() === this.selectedMode.toLowerCase());
        }
      }
      if (this.searchQuery && this.searchQuery.trim()) {
        const q = this.searchQuery.trim().toLowerCase();
        result = result.filter(r => 
          (r.billNo || "").toLowerCase().includes(q) ||
          (r.mode || "").toLowerCase().includes(q)
        );
      }
      return result;
    },
    computedTotalAmount() {
      return this.filteredAdvances.reduce((acc, curr) => acc + (Number(curr.amount) || 0), 0);
    },
    computedCashAmount() {
      return this.filteredAdvances
        .filter(r => (r.mode || "").toLowerCase() === "cash")
        .reduce((acc, curr) => acc + (Number(curr.amount) || 0), 0);
    }
  },
  watch: {
    from() { this.getList(); },
    to() { this.getList(); },
  },
  mounted() {
    this.getList();
  },
  methods: {
    setToday() {
      const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substr(0, 10);
      this.from = today;
      this.to = today;
      this.getList();
    },
    formatCurrency(val) {
      const num = Number(val) || 0;
      return num.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    getModeBadgeClass(mode) {
      const m = (mode || "").toLowerCase();
      if (m.includes("cash")) return "badge-cash";
      if (m.includes("card") || m.includes("upi") || m.includes("bank")) return "badge-digital";
      return "badge-default";
    },
    getList() {
      if (!this.from || !this.to) return;
      this.appLoading = true;
      axios({
        method: "get",
        url: "/data/advancesreport",
        headers: { "x-auth-token": localStorage.getItem("token") },
        params: {
          from: this.from,
          to: this.to,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data && response.data.status) {
            this.advanceRows = response.data.data.advanceRows || [];
            this.totals = response.data.data.totals || { amount: 0 };
          } else {
            this.msg = (response.data && response.data.msg) || "Failed to fetch advances";
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    download() {
      if (!this.from || !this.to) {
        this.msg = "Please choose both dates";
        this.showsnackbar = true;
        return;
      }
      this.appLoading = true;
      axios({
        method: "get",
        url: "/download/advancesreport",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          from: this.from,
          to: this.to,
        },
        responseType: "blob",
      })
        .then((response) => {
          this.appLoading = false;
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `AdvancesReport_${this.from}_to_${this.to}.pdf`);
          document.body.appendChild(link);
          link.click();
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

.mainHeader {
  font-family: 'Cinzel', serif;
  font-size: 22px;
  font-weight: 700;
  color: #1E1815;
  letter-spacing: 0.3px;
}

.btn-today {
  padding: 8px 14px;
  background: #F5EBDC;
  color: #7D551E;
  border: 1px solid rgba(158, 109, 43, 0.25);
  border-radius: 8px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-today:hover {
  background: #9E6D2B;
  color: #FFFFFF;
}

/* KPI CARDS */
.kpi-title {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.8px;
  color: #8C7D6F;
  text-transform: uppercase;
}

.kpi-val {
  font-size: 18px;
  font-weight: 800;
  color: #1E1815;
}

.text-blue { color: #2563EB; }
.text-green { color: #047857; }

/* FILTER & SEARCH */
.filter-search-wrap {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  background: #FFFFFF;
  padding: 10px 14px;
  border: 1px solid #EFE8DD;
  border-radius: 12px;
}

.type-filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.type-pill {
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid #EFE8DD;
  background: #FAF8F5;
  color: #5C5248;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.type-pill:hover {
  background: #F5EBDC;
  color: #7D551E;
}

.type-pill.active {
  background: #9E6D2B;
  color: #FFFFFF;
  border-color: #9E6D2B;
}

.search-input-wrap {
  width: 240px;
}

.no-data-box {
  background: #FFFFFF;
  border: 1px solid #EFE8DD;
  border-radius: 8px;
}

.no-data-text {
  font-size: 14px;
  color: #8C7D6F;
  font-weight: 500;
}

/* TABLE STYLING */
.table-container {
  overflow-x: auto;
  width: 100%;
}

.pro-advances-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13.5px;
  font-family: 'Plus Jakarta Sans', sans-serif;
}

.pro-advances-table th {
  background: #FAF8F5;
  color: #6B5738;
  font-size: 11.5px;
  font-weight: 700;
  padding: 10px 16px;
  vertical-align: middle;
  border-bottom: 1px solid #EFE8DD;
  white-space: nowrap;
  letter-spacing: 0.3px;
}

.pro-advances-table td {
  padding: 11px 16px;
  vertical-align: middle;
  border-bottom: 1px solid #F3EFE8;
  white-space: nowrap;
  color: #1E1815;
}

.table-row:hover td {
  background-color: #FAF5EA !important;
}

.table-row:hover .sticky-col {
  background-color: #FAF5EA !important;
}

.sticky-col {
  position: sticky;
  left: 0;
  z-index: 2;
  background: #FFFFFF !important;
  border-right: 2px solid #EFE8DD !important;
  min-width: 140px;
}

th.sticky-col {
  background: #FAF8F5 !important;
  color: #6B5738 !important;
}

.bill-code-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  background: #F4ECE0;
  color: #7D551E;
  font-size: 12px;
  font-family: monospace;
}

.mode-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 11.5px;
  font-weight: 700;
}

.badge-cash { background: #ECFDF5; color: #047857; }
.badge-digital { background: #EFF6FF; color: #1D4ED8; }
.badge-default { background: #F3F4F6; color: #4B5563; }

.gold-amount-text {
  color: #9E6D2B;
}

.totals-row td {
  background-color: #F7F2E8 !important;
  border-top: 2px solid #9E6D2B !important;
  border-bottom: none !important;
  color: #1E1815;
  font-weight: 700;
  padding: 12px 16px;
}

.total-label-cell {
  color: #9E6D2B !important;
  font-family: 'Cinzel', serif;
  font-size: 13.5px !important;
  background-color: #F7F2E8 !important;
}

.total-amount-cell {
  color: #9E6D2B !important;
  font-size: 14.5px;
}
</style>
